import {
  CustomerSalutation,
  CustomerTitle,
  NationalIdDocumentType,
  VatGroup,
} from '../../../model/customer-information.model';
import { GcdmVatType } from './response.model';

/**
 * GCDM coded values -> CDF domain enums.
 *
 * The write direction lives in the backend (`GcdmSalutation`, `GcdmTitle`, `NationalIdDocumentType`),
 * which prefixes CDF's values. The read direction has to happen here, because the business partner
 * search response is passed straight through to the browser (CUSTAPP-611).
 *
 * Unknown codes map to `undefined` rather than throwing: a record CDF cannot fully interpret must
 * still be selectable for syncing, and the comparison row will simply show it as empty.
 */

const SALUTATIONS: Readonly<Record<string, CustomerSalutation>> = {
  SAL_MR: CustomerSalutation.MR,
  SAL_MS: CustomerSalutation.MS,
  SAL_MX: CustomerSalutation.MX,
  SAL_MRS: CustomerSalutation.MRS,
  SAL_MISS: CustomerSalutation.MISS,
  SAL_DR: CustomerSalutation.DR,
};

const TITLES: Readonly<Record<string, CustomerTitle>> = {
  TIT_DR: CustomerTitle.DR,
  TIT_PROF: CustomerTitle.PROF,
  TIT_PROF_DR: CustomerTitle.PROF_DR,
};

const NATIONAL_ID_DOCUMENT_TYPES: Readonly<Record<string, NationalIdDocumentType>> = {
  PER_IDE_DRIVER_LICENCE: NationalIdDocumentType.DRIVER_LICENCE,
  PER_IDE_DRIVER_LICENSE_PASSPORT_ID: NationalIdDocumentType.DRIVER_LICENSE_ID,
  PER_IDE_IDENTIFICATION_NUMBER: NationalIdDocumentType.IDENTIFICATION_NUMBER,
  PER_IDE_IDENTITY_CARD: NationalIdDocumentType.IDENTITY_CARD,
  PER_IDE_PASSPORT: NationalIdDocumentType.PASSPORT,
  PER_IDE_PASSPORT_ID: NationalIdDocumentType.PASSPORT_ID,
  PER_IDE_PERSON_ID: NationalIdDocumentType.PERSON_ID,
  PER_IDE_SOCIAL_SECURITY_NUMBER: NationalIdDocumentType.SOCIAL_SECURITY_NUMBER,
  PER_IDE_SOUTH_AFRICA_NATIONAL_ID: NationalIdDocumentType.SOUTH_AFRICA_NATIONAL_ID,
};

const VAT_GROUPS: Readonly<Record<GcdmVatType, VatGroup>> = {
  [GcdmVatType.STANDARD]: VatGroup.STANDARD,
  [GcdmVatType.FREE]: VatGroup.NONE,
  [GcdmVatType.REDUCED]: VatGroup.REDUCED,
};

export const fromGcdmSalutation = (code: string | undefined): CustomerSalutation | undefined =>
  code ? SALUTATIONS[code] : undefined;

export const fromGcdmTitle = (code: string | undefined): CustomerTitle | undefined => (code ? TITLES[code] : undefined);

export const fromGcdmNationalIdDocumentType = (code: string | undefined): NationalIdDocumentType | undefined =>
  code ? NATIONAL_ID_DOCUMENT_TYPES[code] : undefined;

export const fromGcdmVatType = (vatType: GcdmVatType | undefined): VatGroup | undefined =>
  vatType ? VAT_GROUPS[vatType] : undefined;
